<template>
  <Teleport to="body">
    <el-drawer v-model="drawerVisible" :size="drawerWidth" direction="rtl" :append-to-body="true"
      :destroy-on-close="false" class="offline-alarm-analysis-drawer-custom" :with-header="true"
      @closed="handleDrawerClosed">
      <!-- 头部：严谨工程级标题与状态 -->
      <template #header>
        <div class="drawer-header-content">
          <div class="header-left">
            <div class="drawer-title-row">
              <v-icon size="20" color="#2655f1" class="mr-2">mdi-robot-outline</v-icon>
              <h3 class="drawer-main-title">{{ drawerMainTitle }}</h3>
            </div>
            <div class="drawer-sub-meta">
              <span class="meta-item">{{ targetStationName }}</span>
              <span class="meta-divider">/</span>
              <span class="meta-item font-weight-bold">{{ targetEquipmentName }}</span>
              <span class="meta-divider">/</span>
              <span class="meta-item code-tag">{{ targetAlarmCode }}</span>
              <span v-if="alarmStartTimeText" class="meta-divider">/</span>
              <span v-if="alarmStartTimeText" class="meta-item">{{ alarmStartTimeText }}</span>
            </div>
          </div>
          <div class="header-actions">
            <v-btn variant="outlined" color="#2655f1" size="small" rounded="pill" class="btn-re-diagnose"
              prepend-icon="mdi-refresh" :loading="phase === 'connecting' || phase === 'running'"
              @click="startAnalysis">
              重新诊断
            </v-btn>
          </div>
        </div>
      </template>

      <!-- 抽屉主体 -->
      <div class="drawer-body-container pa-6 overflow-y-auto">
        <!-- 运行诊断中（统一呈现结构化进度时间线与工具面板，调取数据作为步骤 1 动态呈现，杜绝孤立转圈） -->
        <div v-if="phase === 'loading-context' || phase === 'connecting' || phase === 'running'"
          class="running-container">
          <OfflineProcessTimeline :steps="processSteps" :tools="tools" :is-station-offline="isStationOffline"
            :phase="phase" />
        </div>

        <!-- 阶段三：诊断失败异常 -->
        <div v-else-if="phase === 'failed'" class="state-card failed-state">
          <v-icon size="40" color="#ef4444" class="mb-2">mdi-alert-circle-outline</v-icon>
          <p class="error-title">智能诊断异常中断</p>
          <p class="error-msg">{{ errorMessage }}</p>
          <v-btn color="#2655f1" variant="flat" rounded="pill" size="small" class="text-white mt-3 px-5"
            prepend-icon="mdi-refresh" @click="startAnalysis">
            重新诊断
          </v-btn>
        </div>

        <!-- 阶段四：诊断成功完成（展示五步完整归因成果） -->
        <div v-else-if="phase === 'success'" class="analysis-success-container">
          <OfflineDiagnosisResults :diagnosis="diagnosis" :analysis-id="analysisId" :alarm-record="alarmRecord"
            :telemetry-records="telemetryRecordsData" :current-station-alarms="currentStationAlarmsData"
            :history-alarms="historyAlarmsData" :power-tracking-start="currentPowerTrackingStart"
            :power-tracking-end="currentPowerTrackingEnd" />
        </div>
      </div>
    </el-drawer>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import dayjs from 'dayjs'
import { getAllStationAlertList, getAlertList } from '@/api/alert'
import { getElinkList } from '@/api/station'
import { getDevicesById } from '@/api/device/station'
import { fetchPowerTrackingData } from '@/views/station-monitor/detail/components/data-analysis/powerTrack/api/index.js'
import { getMeterList } from '@/api/meterData'
import { queryMeterAnalysisData } from '@/views/station-monitor/detail/components/data-analysis/meterData/api/index.js'
import { consumeAlarmDiagnosis } from '@/api/alarmDiagnosis'

import OfflineProcessTimeline from './offline-analysis/OfflineProcessTimeline.vue'
import OfflineDiagnosisResults from './offline-analysis/OfflineDiagnosisResults.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  alarmRecord: {
    type: Object,
    default: () => ({})
  },
  tableRecords: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue'])

const drawerVisible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const drawerWidth = computed(() => {
  if (typeof window !== 'undefined' && window.innerWidth < 1400) {
    return '95vw'
  }
  return '1160px'
})

let controller = null
let runSequence = 0

const phase = ref('loading-context') // 'loading-context' | 'connecting' | 'running' | 'success' | 'failed'
const errorMessage = ref('')
const diagnosis = ref(null)
const analysisId = ref('')
const contextId = ref('')
const tools = ref({})

// 业务工程级分析阶段定义（通俗准确，杜绝晦涩黑话）
const DEFAULT_STEPS = [
  { id: 'step-data', text: '调取告警信息与充放电运行数据', status: 'pending', statusDisplay: '' },
  { id: 'step-topo', text: '排查同站其他设备是否同时离线', status: 'pending', statusDisplay: '' },
  { id: 'step-upstream', text: '排查路由器与通信网关等上游链路', status: 'pending', statusDisplay: '' },
  { id: 'step-history', text: '核查近 30 天设备离线记录（判断偶发/频发）', status: 'pending', statusDisplay: '' },
  { id: 'step-llm', text: 'AI 综合分析失联原因并生成处置建议', status: 'pending', statusDisplay: '' },
]

const processSteps = ref(DEFAULT_STEPS.map(s => ({ ...s })))

// 缓存拉取的遥测时序、全站告警与历史记录
const telemetryRecordsData = ref([])
const currentStationAlarmsData = ref([])
const historyAlarmsData = ref([])
const currentPowerTrackingStart = ref('')
const currentPowerTrackingEnd = ref('')

// 头部展示字段
const isStationOffline = computed(() => {
  const code = String(props.alarmRecord?.alarmCode || props.alarmRecord?.code || '').trim()
  const name = String(props.alarmRecord?.description || props.alarmRecord?.alarmName || '').trim()
  const dev = String(props.alarmRecord?.equipmentName || props.alarmRecord?.deviceName || '').trim()
  return code === 'A0_00_04_0001' || name.includes('整站') || !dev || dev === '--'
})
const drawerMainTitle = computed(() => isStationOffline.value ? '整站通信离线智能分析' : '单机通信离线智能分析')
const targetStationName = computed(() => props.alarmRecord?.stationName || props.alarmRecord?.station_name || '储能电站')
const targetEquipmentName = computed(() => {
  if (isStationOffline.value) {
    return '整站通信总出口'
  }
  const raw = props.alarmRecord?.equipmentName || props.alarmRecord?.deviceName || ''
  return raw && raw !== '--' ? raw : '储能受检设备'
})
const targetAlarmCode = computed(() => props.alarmRecord?.alarmCode || props.alarmRecord?.code || 'A0_00_04_0002')
const alarmStartTimeText = computed(() => {
  const t = props.alarmRecord?.startTime || props.alarmRecord?.firstTriggerTime || props.alarmRecord?.firstTime || props.alarmRecord?.start_time || ''
  return t ? dayjs(t).format('YYYY-MM-DD HH:mm:ss') : ''
})

const phaseText = computed(() => {
  if (phase.value === 'loading-context' || phase.value === 'connecting' || phase.value === 'running') return '诊断分析中'
  if (phase.value === 'success') return '诊断完成'
  if (phase.value === 'failed') return '诊断异常'
  return ''
})

const BUILD_INITIAL_TOOLS = () => ({
  'tool-telemetry': {
    id: 'tool-telemetry',
    label: '设备充放电运行数据',
    description: '核对离线前后的 SOC 与充放电功率曲线是否连续',
    status: 'started'
  },
  'tool-station-alarms': {
    id: 'tool-station-alarms',
    label: '同时段电站告警清单',
    description: '检查同一时间是否有其他设备并发离线或告警',
    status: 'pending'
  },
  'tool-sop': {
    id: 'tool-sop',
    label: '现场排查规程 (SOP)',
    description: '匹配供电模块、通信线缆跳线与现场复位排查指引',
    status: 'pending'
  },
  'tool-code-analysis': {
    id: 'tool-code-analysis',
    label: '通信中断机理知识库',
    description: '匹配 RS485 通信总线、网关超时及心跳中断可能原因',
    status: 'pending'
  }
})

function resetState() {
  phase.value = 'loading-context'
  errorMessage.value = ''
  diagnosis.value = null
  analysisId.value = ''
  contextId.value = ''
  tools.value = BUILD_INITIAL_TOOLS()
  processSteps.value = DEFAULT_STEPS.map((s, idx) => ({
    ...s,
    status: idx === 0 ? 'started' : 'pending',
    statusDisplay: idx === 0 ? '调取中...' : ''
  }))
  telemetryRecordsData.value = []
  currentStationAlarmsData.value = []
  historyAlarmsData.value = []
  currentPowerTrackingStart.value = ''
  currentPowerTrackingEnd.value = ''
}

function normalizeAlarmItem(a) {
  if (!a) return null
  const uuid = String(a.uuid || a.deviceVid || a.equipmentUUID || a.device_uuid || '').trim()
  const equipmentName = String(a.equipmentName || a.deviceName || a.equipment_name || '').trim()
  const alarmCode = String(a.alarmCode || a.code || a.alarm_code || '').trim()
  const description = String(a.description || a.alarmDescription || a.alarm_desc || '').trim()
  const startTime = a.startTime || a.firstTriggerTime || a.firstTime || a.start_time || a.checkTime || ''
  const lastTime = a.lastTime || a.lastTriggerTime || a.last_time || startTime
  const endTime = a.endTime && a.endTime !== '/' ? a.endTime : ''
  const category = String(a.category || a.alarmCategory || 'SAFETY').toUpperCase()
  const type = a.type || a.deviceType || a.equipmentType || 'ENERGY_BLOCK'
  return {
    id: a.id,
    stationId: a.stationId ?? a.station_id,
    stationName: a.stationName || a.station_name || '',
    uuid,
    equipmentName,
    alarmCode,
    code: alarmCode,
    description,
    startTime,
    lastTime,
    endTime,
    category,
    type,
    triggerCount: Number(a.triggerCount) || 1
  }
}

function buildOfflineContext(row, extraData = {}) {
  const normRow = normalizeAlarmItem(row) || {}
  const nowStr = dayjs().format('YYYY-MM-DDTHH:mm:ss')
  const dateStr = dayjs(normRow.startTime || normRow.lastTime || new Date()).format('YYYY-MM-DD')
  const stId = Number(extraData.stationId || normRow.stationId) > 0 ? Number(extraData.stationId || normRow.stationId) : 1
  const devUuid = normRow.uuid || 'dev-01'
  const isRecovered = !!normRow.endTime
  const alarmState = isRecovered ? 'RECOVERED' : 'UNRECOVERED'
  const triggerCount = Number(normRow.triggerCount) || 1
  const unrecoveredCount = isRecovered ? 0 : 1

  const currentStationAlarms = extraData.currentStationAlarms || []
  const telemetryRecords = extraData.telemetryRecords || []
  const historyAlarms = extraData.historyAlarms || []

  const alarmGroups = [
    {
      groupId: normRow.alarmCode || 'A0_00_04_0002',
      alarmCode: normRow.alarmCode || 'A0_00_04_0002',
      alarmName: normRow.description || '单机离线',
      description: normRow.description || '单机离线告警',
      category: 'SAFETY',
      severity: 'WARNING',
      alarmRowCount: 1,
      totalTriggerCount: triggerCount,
      unrecoveredCount: unrecoveredCount,
      firstTriggerTime: normRow.startTime ? dayjs(normRow.startTime).format('YYYY-MM-DDTHH:mm:ss') : nowStr,
      lastTriggerTime: normRow.lastTime ? dayjs(normRow.lastTime).format('YYYY-MM-DDTHH:mm:ss') : nowStr,
      sampleRecords: [
        {
          alarmId: Number.isInteger(Number(normRow.id)) && Number(normRow.id) > 0 ? Number(normRow.id) : null,
          sourceAlarmId: normRow.id ? String(normRow.id) : 'rec-01',
          equipmentUuid: devUuid,
          firstTriggerTime: normRow.startTime ? dayjs(normRow.startTime).format('YYYY-MM-DDTHH:mm:ss') : nowStr,
          lastTriggerTime: normRow.lastTime ? dayjs(normRow.lastTime).format('YYYY-MM-DDTHH:mm:ss') : nowStr,
          state: alarmState,
          triggerCount: triggerCount,
          recoverCount: isRecovered ? 1 : 0
        }
      ],
      affectedDeviceUuids: [devUuid],
      totalDevices: 1
    }
  ]

  const devices = [
    {
      equipmentUuid: devUuid,
      equipmentName: normRow.equipmentName || '受检设备',
      deviceType: normRow.type || 'ENERGY_BLOCK',
      capacityKwh: 215.0,
      ratedPowerKw: 100.0,
      installLocation: normRow.equipmentName || ''
    }
  ]

  return {
    contextVersion: '1.0',
    contextId: `ctx-offline-${stId}-${devUuid}-${Date.now()}`,
    source: 'OFFLINE_ANALYSIS_DRAWER',
    generatedAt: nowStr,
    scenario: 'alarm_analysis',
    station: {
      stationId: stId,
      stationName: normRow.stationName || '储能电站',
      stationType: 'INDUSTRIAL_COMMERCIAL',
      gridVoltageLevel: '380V',
      timezone: 'Asia/Shanghai'
    },
    report: {
      reportId: 1,
      reportDate: dateStr,
      dataStartTime: `${dateStr}T00:00:00`,
      dataEndTime: `${dateStr}T23:59:59`,
      timezone: 'Asia/Shanghai'
    },
    summary: {
      alarmTypeCount: 1,
      alarmRowCount: 1,
      totalTriggerCount: Number(normRow.triggerCount) || 1,
      unrecoveredCount: isRecovered ? 0 : 1,
      affectedDeviceCount: 1,
      safetyCount: 1,
      incomeCount: 0,
      normalCount: 0
    },
    alarmGroups,
    devices,
    offlineData: {
      telemetryDataStartTime: extraData.powerTrackingStart,
      telemetryDataEndTime: extraData.powerTrackingEnd,
      currentStationAlarms: currentStationAlarms.map(a => ({
        uuid: a.uuid,
        equipmentName: a.equipmentName,
        alarmCode: a.alarmCode || a.code,
        code: a.code || a.alarmCode,
        description: a.description,
        startTime: a.startTime || a.lastTime,
        category: a.category,
      })),
      telemetryRecords,
      historyAlarms: historyAlarms.map(h => ({
        uuid: h.uuid,
        equipmentName: h.equipmentName,
        alarmCode: h.alarmCode || h.code,
        startTime: h.startTime || h.lastTime,
        endTime: h.endTime,
      })),
      stationEquipments: extraData.stationEquipments || null
    }
  }
}

async function fetchDevicePowerTracking(stId, normRow, tStart, tEnd) {
  if (!stId) return []

  const rowName = (normRow.equipmentName || '').toLowerCase()
  const rowUuid = (normRow.uuid || '').toLowerCase()
  const alarmCode = String(normRow.alarmCode || normRow.code || '')

  // 1. 电表类设备分支：调用专门的电表分析历史曲线接口
  const isMeter = normRow.type === 'METER' ||
    rowName.includes('电表') ||
    rowName.includes('meter') ||
    rowUuid.includes('pcc') ||
    rowUuid.includes('gcp')

  if (isMeter) {
    try {
      let targetMeterUuid = normRow.uuid
      const meterRes = await getMeterList({ stationId: stId })
      const meterRows = Array.isArray(meterRes?.rows) ? meterRes.rows : (Array.isArray(meterRes?.data) ? meterRes.data : [])
      const matched = meterRows.find(m => {
        const mUid = String(m.uuid || m.id || '').toLowerCase()
        const mCode = String(m.meterCode || m.code || '').toLowerCase()
        const mName = String(m.meterName || m.name || '').toLowerCase()
        return (rowUuid && (mUid === rowUuid || mCode === rowUuid)) ||
          (rowName && mName && (rowName.includes(mName) || mName.includes(rowName))) ||
          (rowUuid && mName.includes(rowUuid))
      })
      if (matched && matched.uuid) {
        targetMeterUuid = matched.uuid
      }

      if (targetMeterUuid) {
        const res = await queryMeterAnalysisData({
          stationId: stId,
          uuid: targetMeterUuid,
          startTime: tStart,
          endTime: tEnd
        })
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
        if (list.length > 0) {
          return list.map(item => ({
            record_time: item.record_time,
            p_total: item.p_total !== undefined && item.p_total !== null ? Number(item.p_total) : null,
            q_total: item.q_total !== undefined && item.q_total !== null ? Number(item.q_total) : null,
            pf: item.pf !== undefined && item.pf !== null ? Number(item.pf) : null,
            is_meter: true,
            pcspower_dc: item.p_total !== undefined && item.p_total !== null ? Number(item.p_total) : null,
            batterysystemsoc: null
          }))
        }
      }
    } catch (meterErr) {
      console.warn('[OfflineAnalysis] 电表历史时序数据拉取失败，尝试降级:', meterErr)
    }
  }

  // 2. 整站离线判断（A0_00_04_0001 或设备指向整站）
  const isStationOffline = alarmCode === 'A0_00_04_0001' ||
    rowName.includes('整站') ||
    rowName.includes('储能电站') ||
    rowUuid === 'all' ||
    rowUuid === String(stId)

  // 3. 储能机柜与网关设备（eBlock / eLink / Station）
  let selectType = isStationOffline ? 'station' : 'station'
  let targetElinkId = ''
  let targetEblockId = ''
  let targetElinkSubEblocks = []

  try {
    const elinkRes = await getElinkList(stId)
    const treeList = Array.isArray(elinkRes?.data) ? elinkRes.data : (Array.isArray(elinkRes) ? elinkRes : [])

    // 提前提取整站全量 eLink 和 eBlock ID 数组（对齐 detail/index.vue 官方整站查询逻辑）
    const allStationElinkIDs = []
    const allStationEblockIDs = []
    for (const elk of treeList) {
      const elkId = String(elk.uuid || elk.id || elk.equipmentId || '')
      if (elkId) allStationElinkIDs.push(elkId)
      const children = elk.list || elk.children || []
      if (Array.isArray(children)) {
        for (const eb of children) {
          const ebId = String(eb.uuid || eb.id || eb.equipmentId || '')
          if (ebId) allStationEblockIDs.push(ebId)
        }
      }
    }

    if (!isStationOffline) {
      for (const elk of treeList) {
        const elkName = (elk.name || elk.equipmentName || elk.elinkName || '').toLowerCase()
        const elkId = String(elk.uuid || elk.id || elk.equipmentId || '')
        const children = elk.list || elk.children || []

        // 先匹配是否为单机 eBlock
        if (Array.isArray(children)) {
          for (const eb of children) {
            const ebName = (eb.name || eb.equipmentName || eb.eblockName || '').toLowerCase()
            const ebId = String(eb.uuid || eb.id || eb.equipmentId || '')
            if (rowName.includes(ebName) || rowUuid.includes(ebId.toLowerCase()) || (ebName && rowName.includes(ebName.replace(/#.*$/, '')))) {
              selectType = 'eblock'
              targetElinkId = elkId
              targetEblockId = ebId
              break
            }
          }
        }
        if (selectType === 'eblock') break

        // 若不是具体 eBlock，再匹配是否为 eLink 节点本身
        if (rowName.includes(elkName) || rowUuid.includes(elkId.toLowerCase())) {
          selectType = 'elink'
          targetElinkId = elkId
          targetElinkSubEblocks = (children || []).map(eb => String(eb.uuid || eb.id || eb.equipmentId || '')).filter(Boolean)
        }
      }
    }

    let params = {
      stationId: String(stId),
      startTime: tStart,
      endTime: tEnd
    }

    if (selectType === 'station') {
      params['allElink'] = true
      params['elinkIDs'] = []
      params['allEblock'] = true
      params['eblockIDs'] = []
    } else if (selectType === 'elink') {
      const eblockIDs = targetElinkSubEblocks
      if (eblockIDs.length < 1) {
        return []
      }
      params['allElink'] = false
      params['elinkIDs'] = [targetElinkId]
      params['allEblock'] = true
      params['eblockIDs'] = eblockIDs
    } else if (selectType === 'eblock') {
      params['allElink'] = false
      params['elinkIDs'] = [targetElinkId]
      params['allEblock'] = false
      params['eblockIDs'] = [targetEblockId]
    }

    // 只有在是数组的情况下才 join，避免重复 join 字符串（与 powerTrack/index.js 严格 1:1 保持一致）
    if (Array.isArray(params.eblockIDs)) {
      params.eblockIDs = params.eblockIDs.join(',')
    }
    if (Array.isArray(params.elinkIDs)) {
      params.elinkIDs = params.elinkIDs.join(',')
    }

    const res = await fetchPowerTrackingData(params)
    const dataList = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])
    return dataList
  } catch (err) {
    console.warn('[OfflineAnalysis] 功率跟踪时序数据拉取失败:', err)
    return []
  }
}

async function fetchAuxiliaryData(row) {
  const normRow = normalizeAlarmItem(row) || {}
  const stId = Number(normRow.stationId)
  const stName = normRow.stationName
  const nowStr = dayjs().format('YYYY-MM-DD HH:mm:ss')
  const t30DaysAgo = dayjs().subtract(30, 'day').format('YYYY-MM-DD HH:mm:ss')

  const alertStartTime = normRow.startTime || normRow.lastTime || nowStr
  const alertMoment = dayjs(alertStartTime)
  const causeWindowStart = alertMoment.subtract(3, 'hour').format('YYYY-MM-DD HH:mm:ss')
  const causeWindowEnd = alertMoment.add(30, 'minute').format('YYYY-MM-DD HH:mm:ss')

  const hasEnded = normRow.endTime && normRow.endTime !== '--' && normRow.endTime !== '/' && dayjs(normRow.endTime).isValid()
  const powerTrackingStart = alertMoment.subtract(3, 'hour').format('YYYY-MM-DD HH:mm:ss')
  let powerTrackingEnd = nowStr
  if (hasEnded) {
    const extendEnd = dayjs(normRow.endTime).add(1, 'hour')
    powerTrackingEnd = extendEnd.isAfter(dayjs()) ? nowStr : extendEnd.format('YYYY-MM-DD HH:mm:ss')
  }

  const currentTableAlarms = (props.tableRecords || [])
    .filter(r => {
      const matchId = stId && Number(r.stationId) === stId
      const matchName = stName && r.stationName && (r.stationName === stName || r.stationName.includes(stName) || stName.includes(r.stationName))
      return matchId || matchName
    })
    .map(normalizeAlarmItem)
    .filter(Boolean)

  let serverAlarms = []
  let historyAlarms = []
  let telemetryRecords = []
  let stationEquipments = null

  try {
    const promises = [
      getAllStationAlertList({
        stationId: stId || undefined,
        stationName: stName || undefined,
        lastTimeStart: causeWindowStart,
        lastTimeEnd: causeWindowEnd,
        onlyUnrecovered: null,
        pageNum: 1,
        pageSize: 100
      }),
      getAlertList({
        stationId: stId || undefined,
        startTime: causeWindowStart,
        endTime: causeWindowEnd,
        pageNum: 1,
        pageSize: 100
      }),
      getAlertList({
        stationId: stId || undefined,
        alarmCode: 'A0_00_04_0002',
        startTime: t30DaysAgo,
        endTime: nowStr,
        pageNum: 1,
        pageSize: 500
      }),
      fetchDevicePowerTracking(stId, normRow, powerTrackingStart, powerTrackingEnd),
      stId ? getDevicesById(stId) : Promise.resolve(null)
    ]

    const results = await Promise.allSettled(promises)

    if (results[0].status === 'fulfilled') {
      const r0 = results[0].value
      const list0 = r0?.rows || r0?.data?.rows || r0?.data?.list || (Array.isArray(r0?.data) ? r0.data : (Array.isArray(r0) ? r0 : []))
      serverAlarms = list0.map(normalizeAlarmItem).filter(Boolean)
    }

    if (results[1].status === 'fulfilled') {
      const r1 = results[1].value
      const list1 = r1?.rows || r1?.data?.rows || r1?.data?.list || (Array.isArray(r1?.data) ? r1.data : (Array.isArray(r1) ? r1 : []))
      const extraAlarms = list1.map(normalizeAlarmItem).filter(Boolean)
      serverAlarms = [...serverAlarms, ...extraAlarms]
    }

    if (results[2].status === 'fulfilled') {
      const r2 = results[2].value
      const list2 = r2?.rows || r2?.data?.rows || r2?.data?.list || (Array.isArray(r2?.data) ? r2.data : (Array.isArray(r2) ? r2 : []))
      historyAlarms = list2.map(normalizeAlarmItem).filter(Boolean)
    }

    if (results[3].status === 'fulfilled') {
      const pData = results[3].value
      telemetryRecords = Array.isArray(pData) ? pData : (pData?.data && Array.isArray(pData.data) ? pData.data : (pData?.records || pData?.rows || []))
    }

    if (results[4]?.status === 'fulfilled') {
      const r4 = results[4].value
      stationEquipments = r4?.data || r4 || null
    }
  } catch (err) {
    console.warn('[OfflineAnalysis] 辅助数据拉取异常:', err)
  }

  const currentMergedMap = new Map()
  currentTableAlarms.forEach(a => {
    const key = `${a.uuid || a.equipmentName}_${a.alarmCode}`
    currentMergedMap.set(key, a)
  })
  serverAlarms.forEach(a => {
    const key = `${a.uuid || a.equipmentName}_${a.alarmCode}`
    if (!currentMergedMap.has(key)) {
      currentMergedMap.set(key, a)
    }
  })

  return {
    stationId: stId,
    currentStationAlarms: Array.from(currentMergedMap.values()),
    historyAlarms,
    telemetryRecords,
    powerTrackingStart,
    powerTrackingEnd,
    stationEquipments
  }
}

function delay(ms, runId) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(runId === runSequence)
    }, ms)
  })
}

async function startAnalysis() {
  controller?.abort()
  controller = new AbortController()
  const currentRun = ++runSequence
  resetState()

  try {
    const row = props.alarmRecord
    if (!row || (!row.alarmCode && !row.description)) {
      throw new Error('未获取到有效的告警记录信息')
    }

    const t0 = Date.now()
    // 并行拉取遥测、电站告警、历史告警
    const extraData = await fetchAuxiliaryData(row)
    if (currentRun !== runSequence) return

    telemetryRecordsData.value = extraData.telemetryRecords || []
    currentStationAlarmsData.value = extraData.currentStationAlarms || []
    historyAlarmsData.value = extraData.historyAlarms || []
    currentPowerTrackingStart.value = extraData.powerTrackingStart || ''
    currentPowerTrackingEnd.value = extraData.powerTrackingEnd || ''

    // 步骤 0：调取告警信息与充放电运行数据（确保至少展示 480ms 转动态）
    const elapsed0 = Date.now() - t0
    if (elapsed0 < 480) {
      const ok = await delay(480 - elapsed0, currentRun)
      if (!ok) return
    }
    processSteps.value[0].status = 'completed'
    processSteps.value[0].statusDisplay = '已获取'
    if (tools.value['tool-telemetry']) tools.value['tool-telemetry'].status = 'completed'

    // 步骤 1：排查同站其他设备是否同时离线（真实步进 480ms）
    processSteps.value[1].status = 'started'
    processSteps.value[1].statusDisplay = '排查中...'
    if (tools.value['tool-station-alarms']) tools.value['tool-station-alarms'].status = 'started'
    const ok1 = await delay(480, currentRun)
    if (!ok1) return

    processSteps.value[1].status = 'completed'
    processSteps.value[1].statusDisplay = '已排查'
    if (tools.value['tool-station-alarms']) tools.value['tool-station-alarms'].status = 'completed'

    // 步骤 2：排查路由器与通信网关等上游链路（真实步进 480ms）
    processSteps.value[2].status = 'started'
    processSteps.value[2].statusDisplay = '排查中...'
    if (tools.value['tool-sop']) tools.value['tool-sop'].status = 'started'
    const ok2 = await delay(480, currentRun)
    if (!ok2) return

    processSteps.value[2].status = 'completed'
    processSteps.value[2].statusDisplay = '已排查'
    if (tools.value['tool-sop']) tools.value['tool-sop'].status = 'completed'

    // 步骤 3：核查近 30 天设备离线记录（真实步进 480ms）
    processSteps.value[3].status = 'started'
    processSteps.value[3].statusDisplay = '核查中...'
    if (tools.value['tool-code-analysis']) tools.value['tool-code-analysis'].status = 'started'
    const ok3 = await delay(480, currentRun)
    if (!ok3) return

    processSteps.value[3].status = 'completed'
    processSteps.value[3].statusDisplay = '已核查'
    if (tools.value['tool-code-analysis']) tools.value['tool-code-analysis'].status = 'completed'

    // 步骤 4：AI 综合分析失联原因并生成处置建议
    processSteps.value[4].status = 'started'
    processSteps.value[4].statusDisplay = '分析中...'

    const context = buildOfflineContext(row, extraData)
    contextId.value = context.contextId
    phase.value = 'running'

    await consumeAlarmDiagnosis(context, (evt) => handleDiagnosisEvent(evt, currentRun), controller.signal)
  } catch (err) {
    if (err?.name === 'AbortError' || currentRun !== runSequence) return
    phase.value = 'failed'
    errorMessage.value = err?.message || '智能分析网络异常，请重试'
  }
}

async function handleDiagnosisEvent(event, runId) {
  if (runId && runId !== runSequence) return

  switch (event.type) {
    case 'analysis_started':
      phase.value = 'running'
      analysisId.value = event.analysisId || ''
      contextId.value = event.contextId || contextId.value
      break
    case 'step_detail':
      phase.value = 'running'
      break
    case 'tool_start':
      phase.value = 'running'
      updateTool(event, 'started')
      break
    case 'tool_complete':
      updateTool(event, event.success === false ? 'failed' : 'completed')
      break
    case 'tool_error':
      updateTool(event, 'failed')
      break
    case 'analysis_completed':
      diagnosis.value = event.diagnosis || null
      analysisId.value = event.analysisId || analysisId.value

      // 大模型推理完成，将步骤 4 标记为完成（已生成）
      processSteps.value[4].status = 'completed'
      processSteps.value[4].statusDisplay = '已生成'

      // 所有工具确认就绪
      Object.keys(tools.value).forEach(k => {
        if (tools.value[k].status !== 'failed') {
          tools.value[k].status = 'completed'
        }
      })

      // 稍作停顿 350ms，让用户看清全部步骤打勾完成的圆满状态，随后平滑展开诊断成果
      await delay(350, runId)
      if (runId !== runSequence) return
      phase.value = 'success'
      break
    case 'analysis_failed':
      phase.value = 'failed'
      analysisId.value = event.analysisId || analysisId.value
      errorMessage.value = event.message || '告警智能分析失败'
      break
  }
}

// 严谨适配单机通信离线场景的工具调用解析映射
function updateTool(event, status) {
  const rawId = String(event.tool_id || event.tool_name || '').trim()
  let key = rawId

  if (rawId.includes('sop') || rawId.includes('query_alarm_sop')) {
    key = 'tool-sop'
  } else if (rawId.includes('code') || rawId.includes('query_alarm_code_analysis')) {
    key = 'tool-code-analysis'
  } else if (rawId.includes('telemetry')) {
    key = 'tool-telemetry'
  } else if (rawId.includes('alarm')) {
    key = 'tool-station-alarms'
  }

  const existing = tools.value[key] || {}
  let label = existing.label || '离线辅助信息检索'
  let desc = existing.description || '检索设备底层链路通信状态'

  if (key === 'tool-sop') {
    label = '现场排查规程 (SOP)'
    desc = '匹配供电模块、通信线缆跳线与现场复位排查指引'
  } else if (key === 'tool-code-analysis') {
    label = '通信中断机理知识库'
    desc = '匹配 RS485 通信总线、网关超时及心跳中断可能原因'
  }

  tools.value = {
    ...tools.value,
    [key]: {
      id: key,
      label,
      description: desc,
      status,
      error: event.error || ''
    }
  }
}

function handleDrawerClosed() {
  controller?.abort()
}

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      startAnalysis()
    } else {
      handleDrawerClosed()
    }
  }
)
</script>

<!-- 全局覆盖 Element Plus Teleport 抽屉样式，对齐证据链诊断的紧凑高度与无缝过渡 -->
<style lang="scss">
.offline-alarm-analysis-drawer-custom {
  .el-drawer__header {
    margin-bottom: 0 !important;
    padding: 16px 24px !important;
    border-bottom: 1px solid #e2e8f0 !important;
    display: flex !important;
    align-items: center !important;
  }

  .el-drawer__close-btn {
    margin-left: 18px !important;
    width: 32px !important;
    height: 32px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-radius: 8px !important;
    color: #64748b !important;
    font-size: 18px !important;
    transition: all 0.2s ease !important;
  }

  .el-drawer__body {
    padding: 0 !important;
    background: #f8fafc !important;
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }
}
</style>

<style scoped lang="scss">
.drawer-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  padding-right: 8px;

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .drawer-title-row {
      display: flex;
      align-items: center;

      .drawer-main-title {
        font-size: 16px;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
      }
    }

    .drawer-sub-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #64748b;

      .meta-item {
        display: inline-flex;
        align-items: center;

        &.code-tag {
          background: #eff6ff;
          color: #2655f1;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 600;
        }
      }

      .meta-divider {
        color: #cbd5e1;
      }
    }
  }

  .header-actions {
    display: flex;
    align-items: center;

    .btn-re-diagnose {
      height: 32px !important;
      border-radius: 16px !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      letter-spacing: 0 !important;
      text-transform: none !important;
      padding: 0 14px !important;
      border-color: #b8c5ff !important;
      background: #ffffff !important;
      box-shadow: 0 1px 3px rgba(38, 85, 241, 0.08);
      transition: all 0.2s ease;
    }
  }
}

.drawer-body-container {
  flex: 1;
  background: #f8fafc;
  min-height: 0;

  .state-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 32px 24px;
    text-align: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

    &.loading-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 280px;

      .loading-title {
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;
        margin: 0 0 6px;
      }

      .loading-sub {
        font-size: 12px;
        color: #64748b;
      }
    }

    &.failed-state {
      .error-title {
        font-size: 15px;
        font-weight: 700;
        color: #dc2626;
        margin: 0 0 4px;
      }

      .error-msg {
        font-size: 13px;
        color: #64748b;
        margin: 0;
      }
    }

    &.running-state {
      padding: 12px;
      background: transparent;
      border: none;
      box-shadow: none;
    }
  }

  .spinning-icon {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
}
</style>
